<!-- Initial confirmation state -->
@if (!batchId()) {
  <div class="bulk-renewal-confirmation">
    @if (loadingCount()) {
      <div class="loading-container">
        <p-progressspinner strokeWidth="4" [style]="{ width: '50px', height: '50px' }" />
        <span>Loading eligible systems...</span>
      </div>
    } @else {
      <div class="confirmation-content">
        <p class="description">
          This will renew certificates for <strong>{{ totalFinalSystems() }}</strong> approved systems
          currently in <em>Final</em> state.
        </p>

        <div class="expiry-info">
          <span class="label">New expiry date:</span>
          <span class="value">December 31, {{ nextYear }}</span>
        </div>

        <p-divider />

        <div class="info-note">
          <i class="pi pi-info-circle"></i>
          <span>
            Expiry dates will be updated immediately. Certificate PDFs will be generated
            in the background.
          </span>
        </div>

        <div class="warning-note">
          <i class="pi pi-exclamation-triangle"></i>
          <span>
            <strong>Temporary Feature:</strong> This bulk operation bypasses standard renewal
            workflow checks (e.g., pretreatment license validation). Ensure all systems are
            eligible for renewal before proceeding.
          </span>
        </div>

        <div class="actions">
          <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="close()"
          />
          <p-button
            label="Start Renewal"
            icon="pi pi-refresh"
            [loading]="starting()"
            [disabled]="totalFinalSystems() === 0 || starting()"
            (onClick)="startRenewal()"
          />
        </div>
      </div>
    }
  </div>
}

<!-- Progress state -->
@if (batchId()) {
  <div class="bulk-renewal-progress">
    <div class="progress-header">
      <span class="status-text">
        @if (isComplete()) {
          <i class="pi pi-check-circle success-icon"></i>
          Renewal Complete
        } @else {
          <p-progressspinner strokeWidth="4" [style]="{ width: '20px', height: '20px' }" />
          Generating certificates...
        }
      </span>
    </div>

    <div class="progress-summary">
      <span class="count">{{ status()?.completedCount ?? 0 }} / {{ status()?.totalCount ?? 0 }}</span>
      <p-progressbar [value]="progressPercent()" [showValue]="false" />
    </div>

    <div class="status-details">
      <div class="detail-row">
        <span class="label">Completed:</span>
        <span class="value success">{{ status()?.completedCount ?? 0 }}</span>
      </div>
      @if ((status()?.processingCount ?? 0) > 0) {
        <div class="detail-row">
          <span class="label">Processing:</span>
          <span class="value">{{ status()?.processingCount }}</span>
        </div>
      }
      @if ((status()?.queuedCount ?? 0) > 0) {
        <div class="detail-row">
          <span class="label">Queued:</span>
          <span class="value">{{ status()?.queuedCount }}</span>
        </div>
      }
      @if (hasFailures()) {
        <div class="detail-row">
          <span class="label">Failed:</span>
          <span class="value error">{{ status()?.failedCount }}</span>
        </div>
      }
    </div>

    @if (hasFailures() && status()?.failedItems?.length) {
      <p-divider />
      <div class="failed-items">
        <p class="failed-title">Failed Renewals:</p>
        <ul class="failed-list">
          @for (item of status()!.failedItems; track item.systemId) {
            <li>
              <span class="system-id">{{ item.systemId }}</span>
              <span class="error-message">{{ item.error }}</span>
            </li>
          }
        </ul>
      </div>
    }

    @if (isComplete()) {
      <p-divider />
      <div class="completion-info">
        <p>
          @if (hasFailures()) {
            <i class="pi pi-exclamation-triangle warn-icon"></i>
            Bulk renewal completed with {{ status()?.failedCount }} errors.
          } @else {
            <i class="pi pi-check-circle success-icon"></i>
            All certificates have been generated successfully.
          }
        </p>
        <p class="expiry-note">
          New expiry date: <strong>{{ formatDate(newExpiryDate()) }}</strong>
        </p>
      </div>
    }

    @if (!isComplete()) {
      <div class="warning-note">
        <i class="pi pi-exclamation-triangle"></i>
        <span>
          If you close this dialog, certificate generation will continue in the background,
          but you will not be able to monitor its progress.
        </span>
      </div>
    }

    <div class="actions">
      <p-button
        [label]="isComplete() ? 'Close' : 'Close'"
        severity="secondary"
        [text]="!isComplete()"
        [disabled]="!canClose()"
        (onClick)="close()"
      />
    </div>
  </div>
}
